/**
 * Catalog filter feature — client-held *intent* (search text + sort choice) that is
 * forwarded to the server for actual filtering. The client performs no filtering of
 * its own (untrusted frontend); this hook only manages the controlled inputs.
 */
import { useMemo, useState } from "react";

import {
  DEFAULT_PRODUCT_SORT,
  type ProductListParams,
  type ProductSort,
} from "@entities/product";

export interface CatalogFilterState {
  readonly search: string;
  readonly sort: ProductSort;
  readonly setSearch: (value: string) => void;
  readonly setSort: (value: ProductSort) => void;
  /** Merge the current intent into product-list query params. */
  readonly toParams: (
    base: Pick<ProductListParams, "game" | "category">,
  ) => ProductListParams;
}

export interface UseCatalogFilterOptions {
  readonly initialSort?: ProductSort;
}

/** Manage the shop's search box + sort selector. */
export function useCatalogFilter(
  options: UseCatalogFilterOptions = {},
): CatalogFilterState {
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<ProductSort>(
    options.initialSort ?? DEFAULT_PRODUCT_SORT,
  );

  return useMemo<CatalogFilterState>(
    () => ({
      search,
      sort,
      setSearch,
      setSort,
      toParams: (base) => ({
        ...base,
        sort,
        ...(search.trim().length > 0 ? { search } : {}),
      }),
    }),
    [search, sort],
  );
}
